import dayjs from 'dayjs';
import { Space } from 'antd';
import { debounce } from 'lodash';
import {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/PageHeader';
import useBookings from '../../hooks/useBookings';
import { Filters } from '../../components/Bookings';
import TableBookings, { TableBookingsProps } from '../../components/Bookings/Table';
import { TableActions } from '../../components/TableActions';
import { useWorkspaceStore } from '../../store/workspace.store';
import { IBooking } from '../../interfaces/booking.interface';
import { exportTableData } from '../../utils/export.util';

const BOOKING_COLUMNS = ['timeElapsed', 'bookingNumericId', 'createdAt', 'statusText', 'businessName', 'driverName', 'paymentMethod'];

const ActiveBookings: FC = () => {
    const { t } = useTranslation('bookings', { keyPrefix: 'BOOKINGS' });
    const { t: tb } = useTranslation('bookings', { keyPrefix: 'TABLE' });
    const { t: ts } = useTranslation('bookings', { keyPrefix: 'STATUS' });

    const workspace = useWorkspaceStore((state) => state.data);

    const LIMIT = 10;

    const today = dayjs();
    const [search, setSearch] = useState<string>('');
    const [startDate, setStartDate] = useState<string>(today.startOf('day').toISOString());
    const [endDate, setEndDate] = useState<string>(today.endOf('day').toISOString());
    const [numericId, setNumericId] = useState<string>('');
    const [columnsSelected, setColumnsSelected] = useState<string[]>(BOOKING_COLUMNS);

    const [loading, bookings, total, refreshData, fetchBookings, paginator, props, refreshDate]: any = useBookings({
        typeBookings: 'onlyActives',
        search,
        args: { limit: LIMIT },
        startDate,
        endDate,
    });

    const fetchDebounced = useCallback<any>(debounce(refreshData, 1000), []);
    const fetchDateDebounced = useCallback<any>(debounce(refreshDate, 1000), []);

    const handleSearch = (e: any) => {
        const { value } = e.target;
        setSearch(value);
        fetchDebounced(value, false);
    };

    const handleNumericId = (e: any) => {
        const { value } = e.target;
        setNumericId(value);
        fetchDebounced(value, true);
    };

    const handleDate = (date: string[]) => {
        const [start, end] = date;
        setStartDate(start);
        setEndDate(end);
        fetchDateDebounced([start, end]);
    };

    useEffect(() => {
        if (!workspace?._id) return;
        fetchBookings();
    }, [workspace?._id, paginator]);

    const columnLabels: Record<string, string> = useMemo(() => ({
        timeElapsed: tb('TIME'),
        bookingNumericId: tb('SERVICE_ID'),
        createdAt: tb('DATE'),
        statusText: tb('STATUS'),
        businessName: tb('STORE'),
        driverName: tb('DRIVER'),
        paymentMethod: tb('PAYMENT_METHOD'),
    }), [tb]);

    const handleExport = () => {
        exportTableData<IBooking>(
            bookings,
            [
                { key: 'bookingNumericId', label: tb('SERVICE_ID') },
                { key: 'createdAt', label: tb('DATE'), render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm') },
                { key: 'statusText', label: tb('STATUS'), render: (v) => ts(v) },
                { key: 'businessName', label: tb('STORE') },
                { key: 'driverName', label: tb('DRIVER'), render: (v) => v || tb('NOT_ASSIGNED') },
                { key: 'paymentMethod', label: tb('PAYMENT_METHOD') },
            ],
            `envios-activos-${dayjs().format('YYYY-MM-DD')}`,
        );
    };

    return (
        <div className="screen">
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <PageHeader
                    title={t('ACTIVE_BOOKINGS')}
                    style={{ padding: 0 }}
                />
                <Filters
                    loading={loading}
                    search={search}
                    handleSearch={handleSearch}
                    numericId={numericId}
                    handleNumericId={handleNumericId}
                    startDate={startDate}
                    endDate={endDate}
                    handleDate={handleDate}
                    refreshData={refreshData}
                    extra={(
                        <TableActions
                            columnsSelected={columnsSelected}
                            availableColumns={BOOKING_COLUMNS}
                            columnLabels={columnLabels}
                            onColumnsChange={setColumnsSelected}
                            onExport={handleExport}
                            onReload={() => refreshData()}
                            exportDisabled={!bookings?.length}
                        />
                    )}
                />
                <TableBookings
                    columnsSelected={columnsSelected}
                    loading={loading}
                    total={total}
                    data={bookings}
                    paginator={paginator}
                    refreshData={refreshData}
                    showSort
                    {...props as Partial<TableBookingsProps>}
                />
            </Space>
        </div>
    );
};

export { ActiveBookings };
